import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import theme from '../../styles';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(190, 86, 33, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(190, 86, 33, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(190, 86, 33, 0);
  }
`;

// Chat Bubble Button
export const BubbleButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${theme.color.brick};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  z-index: 1000;
  animation: ${pulse} 2s infinite;

  &:hover {
    transform: scale(1.1);
    background: ${theme.color.brown};
  }

  svg {
    width: 28px;
    height: 28px;
    fill: ${theme.color.light};
  }

  @media (max-width: 600px) {
    bottom: 16px;
    right: 16px;
    width: 56px;
    height: 56px;
  }
`;

// Chat Window Container
export const ChatWindowContainer = styled.div`
  position: fixed;
  bottom: 100px;
  right: 24px;
  width: 380px;
  height: 500px;
  background: ${theme.color.light};
  border-radius: ${theme.borderRadius.large};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;

  @media (max-width: 600px) {
    bottom: 80px;
    right: 8px;
    left: 8px;
    width: auto;
    height: 70vh;
    max-height: 500px;
  }
`;

// Chat Header
export const ChatHeader = styled.div`
  background: ${theme.color.primary};
  color: ${theme.color.light};
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  h3 {
    margin: 0;
    font-family: ${theme.fontFamily.secondary};
    font-size: 16px;
    font-weight: 600;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background 0.2s ease;

  svg {
    width: 20px;
    height: 20px;
    fill: ${theme.color.light};
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Messages Container
export const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${theme.color.cream};
`;

// Message Bubble
export const MessageBubble = styled.div`
  max-width: 85%;
  padding: 12px 16px;
  border-radius: 16px;
  font-family: ${theme.fontFamily.tertiary};
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;

  ${({ role }) =>
    role === 'user'
      ? `
    align-self: flex-end;
    background: ${theme.color.brick};
    color: ${theme.color.light};
    border-bottom-right-radius: 4px;
  `
      : `
    align-self: flex-start;
    background: ${theme.color.light};
    color: ${theme.color.textPrimary};
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  `}
`;

// Typing Indicator
const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-6px);
  }
`;

export const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  padding: 12px 16px;
  background: ${theme.color.light};
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  align-self: flex-start;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  span {
    width: 8px;
    height: 8px;
    background: ${theme.color.brick};
    border-radius: 50%;
    animation: ${bounce} 1.4s infinite ease-in-out;

    &:nth-of-type(1) {
      animation-delay: 0s;
    }
    &:nth-of-type(2) {
      animation-delay: 0.2s;
    }
    &:nth-of-type(3) {
      animation-delay: 0.4s;
    }
  }
`;

// Input Container
export const InputContainer = styled.form`
  display: flex;
  padding: 12px;
  gap: 8px;
  background: ${theme.color.light};
  border-top: 1px solid ${theme.color.beige};
`;

export const ChatInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${theme.color.beige};
  border-radius: 24px;
  font-family: ${theme.fontFamily.tertiary};
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${theme.color.brick};
  }

  &::placeholder {
    color: ${theme.color.textSecondary};
  }
`;

export const SendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${theme.color.brick};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${theme.color.brown};
  }

  &:disabled {
    background: ${theme.color.beige};
    cursor: not-allowed;
  }

  svg {
    width: 20px;
    height: 20px;
    fill: ${theme.color.light};
  }
`;

// Welcome Message
export const WelcomeMessage = styled.div`
  text-align: center;
  padding: 24px;
  color: ${theme.color.textSecondary};
  font-family: ${theme.fontFamily.tertiary};

  h4 {
    margin: 0 0 8px;
    color: ${theme.color.primary};
    font-family: ${theme.fontFamily.secondary};
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;
